import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getColors } from '../theme';
import { 
  ArrowLeft, 
  Smartphone, 
  Zap, 
  Shield, 
  Trash2, 
  HelpCircle,
  Share,
  Plus,
  Check,
  X,
  Rocket,
  DollarSign,
  Code
} from 'lucide-react';

const PWAInfoPage = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  const navigate = useNavigate();

  const Section = ({ icon: Icon, iconColor, title, children }) => (
    <div 
      className="pt-5 pb-2 mb-4"
      style={{ borderTop: `1px solid ${colors.textPrimary}` }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="w-10 h-10 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${iconColor}20` }}
        >
          <Icon size={20} style={{ color: iconColor }} />
        </div>
        <h2 
          className="text-lg font-semibold tracking-tight"
          style={{ color: colors.textPrimary }}
        >
          {title}
        </h2>
      </div>
      <div style={{ color: colors.textSecondary }} className="text-sm leading-relaxed">
        {children}
      </div>
    </div>
  );

  const BulletPoint = ({ icon: Icon, iconColor, positive = true, children }) => (
    <div className="flex items-start gap-3 mb-2">
      <div 
        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: `${iconColor}20` }}
      >
        <Icon size={12} style={{ color: iconColor }} />
      </div>
      <span>{children}</span>
    </div>
  );

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.bg }}
    >
      {/* Header */}
      <div 
        className="sticky top-0 z-40 px-4 py-4"
        style={{ 
          backgroundColor: `${colors.bg}ee`,
          
          
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-md transition-all hover:opacity-70"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <ArrowLeft size={20} style={{ color: colors.textPrimary }} />
          </button>
          <h1 
            className="text-xl font-semibold tracking-tight"
            style={{ color: colors.textPrimary }}
          >
            About Home Screen Apps
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 pt-6">
        
        {/* What is a PWA */}
        <Section icon={Smartphone} iconColor={colors.accent} title="What is a Home Screen App?">
          <p className="mb-3">
            When you add <strong style={{ color: colors.textPrimary }}>mind.</strong> to your home screen, 
            you're installing what's called a <strong style={{ color: colors.textPrimary }}>Progressive Web App (PWA)</strong>.
          </p>
          <p className="mb-4">
            It's a website that looks and feels like a native app, with its own icon, full-screen experience, 
            and quick access right from your home screen.
          </p>
          
          <div className="space-y-1">
            <BulletPoint icon={Zap} iconColor={colors.accent}>
              <strong style={{ color: colors.textPrimary }}>Instant access</strong>: one tap from your home screen
            </BulletPoint>
            <BulletPoint icon={Shield} iconColor={colors.info}>
              <strong style={{ color: colors.textPrimary }}>No app store needed</strong>: skip the download, works immediately
            </BulletPoint>
            <BulletPoint icon={Rocket} iconColor={colors.textSecondary}>
              <strong style={{ color: colors.textPrimary }}>Always up to date</strong>: updates automatically, no manual downloads
            </BulletPoint>
          </div>
        </Section>

        {/* How to Add (iOS) */}
        <Section icon={Share} iconColor={colors.info} title="How to Add on iPhone">
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: colors.info, color: colors.bg }}
              >
                1
              </div>
              <span>Tap the <strong style={{ color: colors.textPrimary }}>Share</strong> button in Safari's toolbar</span>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: colors.info, color: colors.bg }}
              >
                2
              </div>
              <span>Scroll down and tap <strong style={{ color: colors.textPrimary }}>"Add to Home Screen"</strong></span>
            </div>
            <div className="flex items-center gap-3">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: colors.info, color: colors.bg }}
              >
                3
              </div>
              <span>Tap <strong style={{ color: colors.textPrimary }}>Add</strong> in the top right</span>
            </div>
          </div>
          
          {/* iOS Screenshot */}
          <div 
            className="rounded-md overflow-hidden border"
            style={{ borderColor: colors.border }}
          >
            <img 
              src="/screenshots/ios-add-home.png" 
              alt="iOS Add to Home Screen option highlighted in Share menu"
              className="w-full h-auto"
            />
          </div>
        </Section>

        {/* What it's NOT */}
        <Section icon={HelpCircle} iconColor={colors.warning} title="What It's NOT">
          <p className="mb-3">
            A home screen app is <em>not</em> the same as a native app from the App Store or Google Play. 
            Here's how they differ:
          </p>
          
          <div className="space-y-1">
            <BulletPoint icon={X} iconColor={colors.error}>
              <strong style={{ color: colors.textPrimary }}>No push notifications on iOS</strong>: Apple restricts this for web apps (working on it!)
            </BulletPoint>
            <BulletPoint icon={X} iconColor={colors.error}>
              <strong style={{ color: colors.textPrimary }}>No background sync</strong>: the app needs to be open to sync data
            </BulletPoint>
            <BulletPoint icon={X} iconColor={colors.error}>
              <strong style={{ color: colors.textPrimary }}>No offline mode (yet)</strong>: you need an internet connection
            </BulletPoint>
          </div>
          
          <p className="mt-3 text-xs" style={{ color: colors.textMuted }}>
            These are platform limitations, not ours. As web standards evolve, we'll add more native-like features.
          </p>
        </Section>

        {/* How to Remove */}
        <Section icon={Trash2} iconColor={colors.error} title="How to Remove It">
          <p className="mb-3">
            Removing a home screen app is just like deleting any other app icon:
          </p>
          
          <div className="mb-3">
            <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>On iPhone:</p>
            <p>Long-press the icon → Tap "Remove Bookmark" or "Delete Bookmark"</p>
          </div>
          
          <div>
            <p className="font-medium mb-1" style={{ color: colors.textPrimary }}>On Android:</p>
            <p>Long-press the icon → Drag to "Uninstall" or tap the X</p>
          </div>
          
          <p className="mt-3 text-xs" style={{ color: colors.textMuted }}>
            This only removes the shortcut. Your account and data stay safe in the cloud.
          </p>
        </Section>

        {/* Why No Native App */}
        <Section icon={Code} iconColor={colors.textSecondary} title="Why Don't We Have a Native App?">
          <p className="mb-4">
            Great question! Here's the honest answer:
          </p>
          
          <div className="space-y-3">
            <BulletPoint icon={Rocket} iconColor={colors.textSecondary}>
              <strong style={{ color: colors.textPrimary }}>We're in the early stages</strong>: 
              mind. is a young product. We're focused on building the best experience first, 
              then expanding to native apps.
            </BulletPoint>
            
            <BulletPoint icon={Code} iconColor={colors.info}>
              <strong style={{ color: colors.textPrimary }}>Development takes time</strong>: 
              Building separate iOS and Android apps means maintaining 3 codebases. 
              Right now, our small team can move faster with one web app.
            </BulletPoint>
            
            <BulletPoint icon={DollarSign} iconColor={colors.warning}>
              <strong style={{ color: colors.textPrimary }}>The "Apple Tax"</strong>: 
              Apple takes 15-30% of all in-app purchases. By using the web, we can keep 
              prices lower and put more resources into the product.
            </BulletPoint>
          </div>
          
          <div 
            className="mt-4 p-4 rounded-md"
            style={{ backgroundColor: `${colors.accent}10`, border: `1px solid ${colors.accent}30` }}
          >
            <p className="text-sm" style={{ color: colors.textPrimary }}>
              <strong>Native apps are on the roadmap!</strong> As mind. grows, so will our platform support. 
              Your feedback helps us prioritize. Let us know what features matter most to you.
            </p>
          </div>
        </Section>

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="w-full py-3 rounded-md font-medium transition-all hover:opacity-90 active:scale-[0.98] mt-2 mb-4"
          style={{ 
            backgroundColor: colors.bgSecondary,
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
          }}
        >
          ← Go Back
        </button>
      </div>
    </div>
  );
};

export default PWAInfoPage;
